import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../utils/api';
import './CurrencyManagement.css';
import { DollarSign, RefreshCw, TrendingUp, Globe, Edit, Save, X, CheckCircle, Users, ShoppingBag, Store, Clock, ChevronDown } from 'lucide-react';

const CurrencyManagement = () => {
  const [exchangeRates, setExchangeRates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('rates');
  const [editingRate, setEditingRate] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [displayCurrency, setDisplayCurrency] = useState('NGN');

  // All supported currencies with their info
  const currencyData = useMemo(() => ({
    'USD': { symbol: '$', name: 'US Dollar', country: 'United States' },
    'NGN': { symbol: '₦', name: 'Nigerian Naira', country: 'Nigeria' },
    'GHS': { symbol: '₵', name: 'Ghanaian Cedi', country: 'Ghana' },
    'KES': { symbol: 'KSh', name: 'Kenyan Shilling', country: 'Kenya' },
    'TZS': { symbol: 'TSh', name: 'Tanzanian Shilling', country: 'Tanzania' },
    'UGX': { symbol: 'USh', name: 'Ugandan Shilling', country: 'Uganda' },
    'EUR': { symbol: '€', name: 'Euro', country: 'European Union' },
    'GBP': { symbol: '£', name: 'British Pound', country: 'United Kingdom' }
  }), []);

  // Helper function to get currency symbol
  const getCurrencySymbol = useCallback((currency) => {
    return currencyData[currency]?.symbol || currency;
  }, [currencyData]);

  // Helper function to get currency class
  const getCurrencyClass = (currency) => {
    return `currency-${currency.toLowerCase()}`;
  };

  // Get exchange rate for a currency (relative to NGN)
  const getExchangeRate = useCallback((currency) => {
    if (currency === 'NGN') return 1;
    const rate = exchangeRates.find(r => r.currency === currency);
    return rate ? parseFloat(rate.rate) : 1;
  }, [exchangeRates]);

  // Convert amount from NGN to target currency
  const convertFromNGN = useCallback((amountNGN, targetCurrency) => {
    if (targetCurrency === 'NGN') return parseFloat(amountNGN);
    const rate = getExchangeRate(targetCurrency);
    return rate > 0 ? parseFloat(amountNGN) / rate : parseFloat(amountNGN);
  }, [getExchangeRate]);

  // Format currency amount
  const formatCurrency = useCallback((amount, currency) => {
    const symbol = getCurrencySymbol(currency);
    const formatted = parseFloat(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${symbol}${formatted}`;
  }, [getCurrencySymbol]);

  const fetchExchangeRates = useCallback(async () => {
    try {
      const response = await api.get('/admin/currency/exchange-rates');
      if (response.success && response.rates) {
        setExchangeRates(response.rates);
      }
    } catch (err) {
      console.error('Error fetching exchange rates:', err);
      setError('Failed to load exchange rates');
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/admin/stats');
      if (response.success && response.stats) {
        setStats(response.stats);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('Failed to load statistics');
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchExchangeRates(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchExchangeRates, fetchStats]);

  const handleUpdateRates = async () => {
    setUpdating(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/admin/currency/update-rates', {});
      if (response.success) {
        setSuccess(`Successfully updated ${response.rates ? Object.keys(response.rates).length : 0} exchange rates`);
        await fetchExchangeRates();
      } else {
        setError(response.error || 'Failed to update rates');
      }
    } catch (err) {
      console.error('Error updating rates:', err);
      setError('Failed to update exchange rates');
    } finally {
      setUpdating(false);
    }
  };

  const handleEditRate = (rate) => {
    setEditingRate(rate.currency);
    // Convert to "1 foreign = X NGN" format for editing
    const rateValue = parseFloat(rate.rate);
    const displayValue = rateValue < 1 ? (1 / rateValue).toFixed(2) : rateValue.toFixed(4);
    setEditValue(displayValue);
  };

  const handleSaveRate = async () => {
    if (!editingRate || !editValue) return;

    try {
      // Convert from "1 foreign = X NGN" to "1 NGN = X foreign" for storage
      const inputValue = parseFloat(editValue);
      const rateToStore = inputValue > 1 ? (1 / inputValue) : inputValue;

      const response = await api.put(`/admin/currency/exchange-rates/${editingRate}`, {
        rate: rateToStore
      });

      if (response.success) {
        setSuccess(`Updated ${editingRate} exchange rate successfully`);
        await fetchExchangeRates();
        setEditingRate(null);
        setEditValue('');
      } else {
        setError(response.error || 'Failed to update rate');
      }
    } catch (err) {
      console.error('Error saving rate:', err);
      setError('Failed to save exchange rate');
    }
  };

  const handleCancelEdit = () => {
    setEditingRate(null);
    setEditValue('');
  };

  // Calculate total revenue in display currency
  const getTotalRevenueInCurrency = useCallback(() => {
    if (!stats?.totalRevenue) return 0;
    return convertFromNGN(stats.totalRevenue, displayCurrency);
  }, [stats, displayCurrency, convertFromNGN]);

  // Available currencies for switcher
  const availableCurrencies = useMemo(() => {
    const currencies = ['NGN', 'USD'];
    exchangeRates.forEach(rate => {
      if (!currencies.includes(rate.currency)) {
        currencies.push(rate.currency);
      }
    });
    return currencies;
  }, [exchangeRates]);

  if (loading) {
    return (
      <div className="currency-container">
        <div className="currency-loading">
          <div className="loading-spinner"></div>
          <p>Loading currency data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="currency-container">
      {/* Header */}
      <div className="currency-header">
        <div className="header-left">
          <div className="header-icon">
            <Globe size={24} />
          </div>
          <div className="header-text">
            <h1>Currency Management</h1>
            <p>Manage exchange rates and view multi-currency statistics</p>
          </div>
        </div>
        <div className="header-actions">
          <button
            onClick={handleUpdateRates}
            disabled={updating}
            className="btn-primary"
          >
            <RefreshCw size={18} className={updating ? 'spinning' : ''} />
            {updating ? 'Updating...' : 'Update Rates'}
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error">
          <X size={18} />
          <span>{error}</span>
          <button onClick={() => setError('')} className="alert-close">
            <X size={14} />
          </button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <CheckCircle size={18} />
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="alert-close">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="currency-tabs">
        <button
          className={`tab ${activeTab === 'rates' ? 'active' : ''}`}
          onClick={() => setActiveTab('rates')}
        >
          <DollarSign size={18} />
          Exchange Rates
        </button>
        <button
          className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          <TrendingUp size={18} />
          Multi-Currency Statistics
        </button>
      </div>

      {/* Exchange Rates Tab */}
      {activeTab === 'rates' && (
        <div className="rates-section">
          <div className="rates-grid">
            {exchangeRates.map((rate) => {
              const currencyInfo = currencyData[rate.currency] || { symbol: rate.currency, name: rate.currency, country: '' };
              return (
                <div key={rate.currency} className={`rate-card ${getCurrencyClass(rate.currency)}`}>
                  <div className="rate-header">
                    <div className={`rate-icon ${getCurrencyClass(rate.currency)}`}>
                      {currencyInfo.symbol}
                    </div>
                    <div className="rate-info">
                      <h3>{currencyInfo.name}</h3>
                      <span className="rate-code">{rate.currency} - {currencyInfo.country}</span>
                    </div>
                    {editingRate !== rate.currency && (
                      <button
                        onClick={() => handleEditRate(rate)}
                        className="btn-edit"
                        title="Edit rate"
                      >
                        <Edit size={16} />
                      </button>
                    )}
                  </div>

                  <div className="rate-body">
                    {editingRate === rate.currency ? (
                      <div className="rate-edit-form">
                        <label>1 {currencyInfo.symbol} equals how many Naira?</label>
                        <div className="rate-input-group">
                          <span className="input-prefix">₦</span>
                          <input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            step="0.01"
                            placeholder="e.g. 1600 for USD"
                            autoFocus
                          />
                        </div>
                        <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                          Enter the NGN value (e.g., 1600 means 1 {rate.currency} = ₦1,600)
                        </p>
                        <div className="rate-edit-actions">
                          <button onClick={handleSaveRate} className="btn-save">
                            <Save size={16} />
                            Save
                          </button>
                          <button onClick={handleCancelEdit} className="btn-cancel">
                            <X size={16} />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="rate-display">
                        <span className="rate-label">1 {currencyInfo.symbol} =</span>
                        <span className="rate-value">
                          ₦{(parseFloat(rate.rate) < 1
                            ? (1 / parseFloat(rate.rate)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            : parseFloat(rate.rate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          )}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="rate-footer">
                    <span className={`source-badge ${rate.source === 'API' ? 'api' : 'manual'}`}>
                      {rate.source || 'MANUAL'}
                    </span>
                    <span className="update-time">
                      <Clock size={12} />
                      Updated {new Date(rate.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Statistics Tab */}
      {activeTab === 'stats' && stats && (
        <div className="stats-section">
          {/* Currency Switcher */}
          <div className="currency-switcher">
            <label>Display currency:</label>
            <div className="switcher-select-wrapper">
              <select
                value={displayCurrency}
                onChange={(e) => setDisplayCurrency(e.target.value)}
                className="switcher-select"
              >
                {availableCurrencies.map(currency => (
                  <option key={currency} value={currency}>
                    {getCurrencySymbol(currency)} {currency} - {currencyData[currency]?.name || currency}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} className="select-arrow" />
            </div>
          </div>

          {/* Overview Stats */}
          <div className="stats-overview">
            <div className="stat-card revenue">
              <div className="stat-icon">
                <DollarSign size={24} />
              </div>
              <div className="stat-content">
                <span className="stat-label">Total Revenue</span>
                <span className="stat-value">{formatCurrency(getTotalRevenueInCurrency(), displayCurrency)}</span>
                {displayCurrency !== 'NGN' && (
                  <span className="stat-note">Converted from NGN</span>
                )}
              </div>
            </div>

            <div className="stat-card merchants">
              <div className="stat-icon">
                <Users size={24} />
              </div>
              <div className="stat-content">
                <span className="stat-label">Total Merchants</span>
                <span className="stat-value">{stats.totalMerchants || 0}</span>
              </div>
            </div>

            <div className="stat-card orders">
              <div className="stat-icon">
                <ShoppingBag size={24} />
              </div>
              <div className="stat-content">
                <span className="stat-label">Total Orders</span>
                <span className="stat-value">{stats.totalOrders || 0}</span>
              </div>
            </div>

            <div className="stat-card products">
              <div className="stat-icon">
                <Store size={24} />
              </div>
              <div className="stat-content">
                <span className="stat-label">Total Products</span>
                <span className="stat-value">{stats.totalProducts || 0}</span>
              </div>
            </div>
          </div>

          {/* Merchants by Currency */}
          {stats.merchantsByCurrency && stats.merchantsByCurrency.length > 0 && (
            <div className="stats-block">
              <div className="block-header">
                <Users size={20} />
                <h2>Merchants by Currency</h2>
              </div>
              <div className="merchants-grid">
                {stats.merchantsByCurrency.map((item) => {
                  const currencyInfo = currencyData[item.currency] || { symbol: item.currency, name: item.currency, country: '' };
                  return (
                    <div key={item.currency} className={`merchant-card ${getCurrencyClass(item.currency)}`}>
                      <div className={`merchant-icon ${getCurrencyClass(item.currency)}`}>
                        {currencyInfo.symbol}
                      </div>
                      <div className="merchant-info">
                        <span className="merchant-currency">{item.currency}</span>
                        <span className="merchant-count">{item.count} merchants</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Revenue by Currency */}
          <div className="stats-block">
            <div className="block-header">
              <TrendingUp size={20} />
              <h2>Revenue by Currency</h2>
              <span className="block-subtitle">Showing in {displayCurrency}</span>
            </div>
            {stats.revenueByCurrency && stats.revenueByCurrency.length > 0 ? (
              <div className="revenue-grid">
                {stats.revenueByCurrency.map((item) => {
                  const currencyInfo = currencyData[item.currency] || { symbol: item.currency, name: item.currency, country: '' };
                  const originalAmount = parseFloat(item.amount);
                  const ngnAmount = parseFloat(item.amountNGN);
                  const displayAmount = convertFromNGN(ngnAmount, displayCurrency);

                  return (
                    <div key={item.currency} className={`revenue-card ${getCurrencyClass(item.currency)}`}>
                      <div className="revenue-header">
                        <div className={`revenue-icon ${getCurrencyClass(item.currency)}`}>
                          {currencyInfo.symbol}
                        </div>
                        <div className="revenue-title">
                          <h4>{currencyInfo.name}</h4>
                          <span>{item.currency}</span>
                        </div>
                      </div>
                      <div className="revenue-body">
                        <div className="revenue-row original">
                          <span className="revenue-label">Original Amount</span>
                          <span className="revenue-amount">{currencyInfo.symbol}{originalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="revenue-row converted">
                          <span className="revenue-label">In {displayCurrency}</span>
                          <span className="revenue-amount">{formatCurrency(displayAmount, displayCurrency)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <TrendingUp size={40} />
                <p>No revenue data available yet</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrencyManagement;
